import { useTranslation } from 'react-i18next';
import type { IChatIntegration } from '@interfaces/IChatIntegrations';
import { Button } from '@components/ui/Button/Button';
import { KebabButton } from '@components/ui/KebabButton/KebabButton';
import { Menu } from '@components/ui/Menu/Menu';
import { Pill } from '@components/ui/Pill/Pill';
import { StatusDot } from '@components/ui/StatusDot/StatusDot';
import { Switch } from '@components/ui/Switch/Switch';
import { PROVIDERS } from '../../providers';
import styles from './AppRow.module.css';

const LOGO_SIZE = 22;

interface IAppRowProps {
  app: IChatIntegration;
  saving: boolean; // A change to this app is saving: its switch and Disconnect wait for it
  onConnect: () => void;
  onEnabledChange: (enabled: boolean) => void;
  onDisconnect: () => void;
}

/** One app in the connect menu: coming soon, ready to connect, connected, or broken. */
export function AppRow({ app, saving, onConnect, onEnabledChange, onDisconnect }: IAppRowProps) {
  const { t } = useTranslation();
  const { name, Logo } = PROVIDERS[app.provider];
  const { connection } = app;

  if (!app.available) {
    return (
      <div className={styles.row}>
        <span className={`${styles.logo} ${styles.muted}`}><Logo size={LOGO_SIZE} /></span>
        <span className={`${styles.text} ${styles.name} ${styles.nameMuted}`}>{name}</span>
        <Pill className={styles.comingSoon}>{t('comms.integrations.comingSoon')}</Pill>
      </div>
    );
  }

  return (
    <div className={styles.row}>
      <span className={styles.logo}><Logo size={LOGO_SIZE} /></span>
      <span className={styles.text}>
        <span className={styles.name}>{name}</span>
        <span className={styles.line}>
          {connection === null && t(`comms.integrations.where.${app.provider}`)}
          {connection?.status === 'ok' && (
            <>
              <StatusDot color="var(--color-success-mid)" />
              {t('comms.integrations.connectedTo', { account: connection.accountLabel })}
            </>
          )}
          {connection?.status === 'broken' && (
            <>
              <StatusDot color="var(--color-error)" />
              {t('comms.integrations.brokenLine', { app: name })}
            </>
          )}
        </span>
      </span>
      {connection === null && (
        <Button variant="primary" size="sm" onClick={onConnect}>{t('comms.integrations.connect')}</Button>
      )}
      {connection?.status === 'ok' && (
        <Switch
          checked={connection.enabled}
          disabled={saving}
          onCheckedChange={onEnabledChange}
          ariaLabel={t('comms.integrations.notesSwitch', { app: name })}
        />
      )}
      {connection?.status === 'broken' && (
        <Button variant="primary" size="sm" onClick={onConnect}>{t('comms.integrations.reconnect')}</Button>
      )}
      {connection !== null && (
        <Menu.Root>
          <Menu.Trigger render={<KebabButton aria-label={t('comms.integrations.appMenu', { app: name })} />} />
          <Menu.Portal>
            <Menu.Positioner side="bottom" align="end" sideOffset={4}>
              <Menu.Popup>
                <Menu.Item className={styles.disconnect} disabled={saving} onClick={onDisconnect}>
                  {t('comms.integrations.disconnect')}
                </Menu.Item>
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      )}
    </div>
  );
}
